/**
 * web/src/screens/Notebook/capture.tsx — the one capture bar (its pop-in
 * sheet and compact row) and the confirmations that follow a save: the link
 * receipt, the saved-with-Undo toast and the quiet-remove Undo line (card
 * vncVrGk1hc9i: moved out of index.tsx unchanged; all state arrives as props).
 */
import type { ReactNode } from 'react';
import { api } from '../../shell/bridge';
import { Pressable } from '../../ui/Pressable';
import { Composer } from '../../components/Composer';
import { Tooltip } from '../../ui/Tooltip';
import { Overlay } from '../../ui/Overlay';
import { notebookRouteFor } from '../../../../shared/types';
import { useNotebookState, type NotebookState } from './hooks';
import { ENTRY_LABEL, ENTRY_PLURAL, CAPTURE_TYPES } from './types';
import { ENTRY_ICON, ICON_ATTRS } from './icons';


export function CaptureBar({ s,  }: { s: NotebookState }) {
  const { captureOpen, dismissCapture, captureSave, captureKeyDown, draft, setDraft, setQuickSaved, captureType, typeMenuOpen, setTypeMenuOpen, setQuickType, draftSharedFor, setSharedDraft, setGiftDraft, adding, quickError, captureError, quickSaved, closeCaptureNow, openCapture, partnerLabel } = s;
  return (
    <>
      {/* Card 9wlZ8NbjACIY (Jonathan 2026-10-24) — the EXPANDED state is a
          pop-in SHEET, not an inline stretch of the sticky bar: the compact
          row stays exactly as it is, one tap pops the full form in over the
          feed, and the whole element UI (field, type chip + menu, help tip,
          who-can-see, Save, error/saved lines) gets the panel to itself.

          It is a POP-IN, not a modal: the backdrop is transparent and
          click-through, so the feed behind stays readable and scrollable and
          the sheet does not ambush a tap meant for a card or a shelf tab.
          Tapping away dismisses it with the draft intact (only the X and
          Escape throw words away), the same promise the compact bar has.
          Same ids, same save path, same X and Escape — only the container
          moved, into the app's one overlay primitive. */}
      <Overlay
        open={captureOpen}
        onClose={dismissCapture}
        className="nb-capture-overlay"
        panelClassName="more-sheet nb-capture-sheet"
        panelTestId="nb-capture-sheet"
        label="Save a link, idea, gift or memory"
        initialFocus="#nb-quick"
        trap={false}
        inertBackground={false}
        lockScroll={false}
        dismissOnBackdrop={false}
        closeOnOutsidePointer
      >
      <form className="nb-capture" data-testid="nb-capture" onSubmit={captureSave} onKeyDown={captureKeyDown}>
        <Composer
          id="nb-quick"
          bare
          dataTest="nb-quick-input"
          inputClassName="pear-chat-input nb-input nb-capture-input"
          placeholder="Save a link, idea, gift or memory…"
          ariaLabel={`Capture a ${ENTRY_LABEL[captureType].toLowerCase()}`}
          value={draft}
          enterBehavior="submit"
          onChange={v => { setDraft(v); setQuickSaved(null); }}
        />
        <div className="nb-capture-row">
          {/* C2 — the detected type, editable. A URL always reads as a link
              (a link cannot be words); everything else can be re-typed. */}
          <div className="nb-chip-wrap">
            <button
              type="button"
              className={`nb-type-chip nb-type-${captureType}`}
              data-test="nb-type-chip"
              aria-haspopup="menu"
              aria-expanded={typeMenuOpen}
              aria-label={`Saving as ${ENTRY_LABEL[captureType]} — change the type`}
              onClick={() => setTypeMenuOpen(o => !o)}
            >
              <span className="nb-icon" aria-hidden="true">{ENTRY_ICON[captureType]}</span>
              {ENTRY_LABEL[captureType]}
              <span className="nb-chip-caret" aria-hidden="true">▾</span>
            </button>
            {typeMenuOpen && (
              <div className="nb-type-menu" role="menu" aria-label="Save as">
                {CAPTURE_TYPES.map(t => (
                  <button
                    key={t}
                    type="button"
                    role="menuitemradio"
                    aria-checked={captureType === t}
                    className={`nb-type-opt${captureType === t ? ' is-on' : ''}`}
                    data-test={`nb-type-${t}`}
                    onClick={() => { setQuickType(t); setTypeMenuOpen(false); }}
                  >
                    <span className="nb-icon" aria-hidden="true">{ENTRY_ICON[t]}</span>
                    {ENTRY_LABEL[t]}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* mG5eA32qztRg (Jonathan 2026-10-24) — the hint paragraph that
              duplicated the placeholder under the bar is GONE. What it carried
              that a placeholder cannot (the four types, that Pear files it for
              you, who each visibility setting shares it with) lives one tap
              away, beside the chip that picks the type. */}
          <Tooltip label="Save a link, an idea, a gift or a memory — Pear files it for you. Shared means ${partnerLabel} sees it too; 'Just you' keeps it off their list.">
          </Tooltip>
          {/* mG5eA32qztRg — one right-aligned cluster: the who-can-see control
              and Save share the same right edge, so 'Just you' lines up with
              Save instead of floating between the chip and the margin. */}
          <div className="nb-capture-actions">
          {/* C3 — Shared / Just me, default Shared (card sYwPmYEIf4VC). A date
              idea has no private version: it enters the couple's own planning
              pipeline. A GIFT defaults to Just me and keeps its OWN switch
              (card ggTQe2KbB0A3 HIGH-3), so a surprise cannot be widened by a
              note saved a moment earlier. */}
          {captureType !== 'date' && (
            <div className="nb-visibility" role="radiogroup" aria-label="Who can see this entry">
              <button
                type="button"
                role="radio"
                id="nb-vis-us"
                aria-checked={draftSharedFor(captureType) === 'us'}
                className={`nb-vis-btn${draftSharedFor(captureType) === 'us' ? ' is-on' : ''}`}
                onClick={() => (captureType === 'gift' ? setGiftDraft('us') : setSharedDraft('us'))}
              >
                Shared
              </button>
              <button
                type="button"
                role="radio"
                id="nb-vis-me"
                aria-checked={draftSharedFor(captureType) === 'me'}
                className={`nb-vis-btn${draftSharedFor(captureType) === 'me' ? ' is-on' : ''}`}
                onClick={() => (captureType === 'gift' ? setGiftDraft('me') : setSharedDraft('me'))}
              >
                Just you
              </button>
            </div>
          )}
          {/* PRD §6.7 D7 — gold is the ACTION role, and Save is the one action
              on this surface, so it is the only mustard fill here. */}
          <button
            type="submit"
            data-test="nb-quick-add"
            className="pear-btn pear-btn-mustard nb-add nb-capture-save"
            disabled={adding || draft.trim().length === 0}
          >
            {adding ? 'Saving…' : 'Save'}
          </button>
          </div>
          {/* The one dismiss path the founder asked for (email 2026-10-21).
              type="button" so it can never submit, and it discards the draft
              through the same reset Escape uses — no row is written. Stays
              outside the Save cluster: the dismiss is not part of the action
              group (mG5eA32qztRg). */}
          <button
            type="button"
            className="nb-capture-close"
            data-test="nb-capture-close"
            aria-label="Close the capture bar"
            onClick={closeCaptureNow}
          >
            <svg {...ICON_ATTRS}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
          </button>
        </div>
        {/* mG5eA32qztRg — the nb-capture-hint paragraph is gone: it said what
            the placeholder already says. Its per-type/visibility copy lives in
            the (?) tooltip beside the chip above. */}
        {quickError && (
          <p className="nb-save-error" data-test="nb-quick-error" role="alert">{quickError}</p>
        )}
        {captureError && (
          <p className="nb-save-error" data-test="nb-link-error" role="alert">{captureError}</p>
        )}
        {quickSaved && (
          <p className="nb-saved" data-test="nb-saved" role="status">
            Saved to your {ENTRY_PLURAL[quickSaved].toLowerCase()} — ready for the next one.
          </p>
        )}
      </form>
      </Overlay>
        {/* Compact (card mG5eA32qztRg): the closed bar reads as a FIELD, not a
           note to self — the placeholder text Jonathan's eye expects, with the
           mustard + at the right edge as the one action. Same 44px floor, same
           slot, so the sticky block never jumps between the two states. It is
           the only thing left in this block: the form above it lives in the
           sheet (card 9wlZ8NbjACIY), so it stays put while the sheet is up. */}
        <button
          type="button"
          className="nb-capture-reopen"
          data-test="nb-capture-reopen"
          aria-label="Save a link, idea, gift or memory — open the capture bar"
          onClick={openCapture}
        >
          <span className="nb-capture-reopen-text">Save a link, idea, gift or memory…</span>
          <span className="nb-capture-plus" aria-hidden="true">+</span>
        </button>
    </>
  );
}

export function Receipt({ s,  }: { s: NotebookState }) {
  const { receipt, flipReceiptScope, undoReceipt, setReceipt } = s;
  return (
    <>
        {/* The receipt (card VPlGeOn7LJPI, PRD §6.1): where the paste filed, who
            can see it, one honest change, and the undo a mis-paste needs. It
            lives under the composer, not in a floating toast, because the
            composer is where the paste happened and the next one happens. */}
        {receipt && (
          <div className="nb-receipt" data-test="nb-receipt" role="status">
            <p className="nb-receipt-text" data-test="nb-receipt-text">
              {receipt.undone
                ? 'Removed — that link is gone.'
                : receipt.filing
                  ? 'Saved. Still reading the link to file it…'
                  /* Card xNGOA5rZz9LV phase 3: the band is part of the receipt
                     now. An unsure verdict used to read exactly like a
                     confident one — same "Filed to …" sentence — so the one
                     case that needs a person looked like the case that did not. */
                  : receipt.tier === 'ask'
                    ? `Saved — nothing confident filed this one · ${receipt.scope === 'shared' ? 'Shared with them' : 'Just you'}. Pick its home on the card below.`
                    : receipt.tier === 'check'
                      ? `Filed to ${notebookRouteFor(receipt.kind).homeLabel} · worth a check · ${receipt.scope === 'shared' ? 'Shared with them' : 'Just you'}.`
                      : `Filed to ${notebookRouteFor(receipt.kind).homeLabel} · ${receipt.scope === 'shared' ? 'Shared with them' : 'Just you'}.`}
            </p>
            {!receipt.undone && (
              <div className="nb-receipt-actions">
                <button
                  type="button"
                  data-test="nb-receipt-change"
                  className="pear-btn pear-btn-quiet nb-receipt-btn"
                  disabled={receipt.filing || receipt.busy}
                  onClick={() => void flipReceiptScope()}
                >
                  {receipt.scope === 'shared' ? 'Make it just you' : 'Share it with them'}
                </button>
                <button
                  type="button"
                  data-test="nb-receipt-undo"
                  className="pear-btn pear-btn-quiet nb-receipt-btn"
                  disabled={receipt.busy}
                  onClick={() => void undoReceipt()}
                >
                  {receipt.busy ? 'Working…' : 'Undo'}
                </button>
              </div>
            )}
            {receipt.error && (
              <p className="nb-receipt-error" data-test="nb-receipt-error" role="alert">{receipt.error}</p>
            )}
            <button
              type="button"
              data-test="nb-receipt-dismiss"
              className="nb-receipt-x"
              aria-label="Dismiss this receipt"
              onClick={() => setReceipt(null)}
            >
              ✕
            </button>
          </div>
        )}
    </>
  );
}

export function CaptureToast({ s,  }: { s: NotebookState }) {
  const { captureToast, undoCapture, setCaptureToast } = s;
  return (
    <>
        {/* C5 — the saved-with-Undo line. A capture is a one-field action, so
            its confirmation has to be undoable in the same breath; the row is
            removed outright by its own id, never "the newest one". */}
        {captureToast && (
          <div className="nb-capture-toast" data-test="nb-capture-toast" role="status">
            <span className="nb-capture-toast-text">{captureToast.text}</span>
            {captureToast.itemId && (
              <button
                type="button"
                className="nb-undo-btn"
                data-test="nb-capture-undo"
                onClick={() => void undoCapture(captureToast.itemId as string)}
              >
                Undo
              </button>
            )}
            <button
              type="button"
              className="nb-receipt-x"
              aria-label="Dismiss"
              onClick={() => setCaptureToast(null)}
            >
              {/* Vectors only in the panel (walk-notebook-pills): the ✕ glyph
                  is a dingbat the "no emoji" sweep rightly catches, so the
                  toast dismiss draws the same X as an SVG. */}
              <svg {...ICON_ATTRS} width={12} height={12}>
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        )}
    </>
  );
}

export function UndoLine({ s,  }: { s: NotebookState }) {
  const { removedItem, busyId, undoRemove } = s;
  return (
    <>
        {/* Card xUZFWf8JtHo2 item 5 — the quiet remove's safety net. Removing a
            card leaves this line for a few seconds; Undo puts the row back. */}
        {removedItem && (
          <div className="nb-undo" data-test="nb-undo" role="status">
            <span className="nb-undo-text">
              Removed “{removedItem.label.length > 48 ? `${removedItem.label.slice(0, 48)}…` : removedItem.label}”.
            </span>
            <button
              type="button"
              className="nb-undo-btn"
              data-test="nb-undo-btn"
              disabled={busyId !== null}
              onClick={() => void undoRemove()}
            >
              Undo
            </button>
          </div>
        )}
    </>
  );
}
